import { useEffect, useState } from 'react'
import axios from 'axios'
import { CheckCircle2, XCircle, Loader2, Copy, Check, X } from 'lucide-react'
import { Button } from '../components/ui/button'
import { useLanguage } from '../contexts/LanguageContext'
import { formatMoney, getApiUrl } from '../lib/utils'
import { burstConfetti } from '../components/celebration'

const API = getApiUrl()

const PaymentComplete = () => {
  const { language } = useLanguage()
  const tr = (en: string, ka: string) => (language === 'ka' ? ka : en)

  const params = new URLSearchParams(window.location.search)
  const id = params.get('id') || ''

  const [data, setData] = useState<any>(null)
  const [phase, setPhase] = useState<'loading' | 'paid' | 'failed' | 'pending'>('loading')
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    if (!id) {
      setPhase('failed')
      return
    }
    // Every checkout is a one-off Payment now — gifts, AI credits and the
    // storage add-ons all land on the same public status endpoint.
    const statusUrl = `${API}/public/payments/${id}/status`

    let stopped = false
    let attempts = 0
    const isPaid = (d: any) => d.status === 'paid'
    const isFailed = (d: any) =>
      ['failed', 'cancelled', 'reversed', 'declined', 'expired'].includes(d.status)

    const check = async () => {
      if (stopped) return
      try {
        const res = await axios.get(statusUrl)
        if (stopped) return
        setData(res.data)
        if (isPaid(res.data)) return setPhase('paid')
        if (isFailed(res.data)) return setPhase('failed')
        setPhase('pending')
      } catch (e) {
        /* keep trying */
      }
      if (++attempts >= 40) return // ~100s
      setTimeout(check, 2500)
    }
    check()
    return () => {
      stopped = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Celebration burst the moment the payment lands. burstConfetti is a no-op
  // when the user prefers reduced motion.
  useEffect(() => {
    if (phase === 'paid') burstConfetti()
  }, [phase])

  const txId = data?.transaction_id || data?.payment_id || id
  const amount = data?.amount
  const currency = data?.currency || 'GEL'

  const copyDetails = () => {
    const lines = [
      `Transaction: ${txId}`,
      amount != null ? `Amount: ${formatMoney(amount)} ${currency}` : null,
      data?.masked_card ? `Card: ${data.masked_card}` : null,
      `Reference: ${id}`,
    ].filter(Boolean)
    navigator.clipboard.writeText(lines.join('\n'))
    setCopied(true)
    setTimeout(() => setCopied(false), 1800)
  }

  const close = () => {
    // Inside the payment overlay iframe: ask the parent app to dismiss it.
    if (window.parent && window.parent !== window) {
      window.parent.postMessage('storyfy:payment-close', '*')
      return
    }
    // Standalone (e.g. popup-blocker fallback redirect): try to close, else go home.
    window.close()
    setTimeout(() => {
      if (!window.closed) window.location.href = '/'
    }, 300)
  }

  return (
    <div className="min-h-[100dvh] bg-story-gradient flex items-center justify-center p-4">
      <div className="w-full max-w-sm bg-white dark:bg-[#1e1438] border border-white/60 dark:border-white/10 rounded-bubble-lg shadow-celebrate-lg motion-safe:animate-pop-in p-7 text-center">
        {phase === 'loading' || phase === 'pending' ? (
          <>
            <Loader2 className="w-10 h-10 animate-spin text-[#ff4d8d] mx-auto mb-4" />
            <h1 className="text-xl font-bold text-[#1b1033] dark:text-[#f4f0ff] font-display">
              {tr('Confirming your payment…', 'ვადასტურებთ გადახდას…')}
            </h1>
            <p className="text-sm text-[#4a3d66] dark:text-[#c3b8dd] mt-2">
              {tr('This can take a few seconds.', 'ამას შეიძლება რამდენიმე წამი დასჭირდეს.')}
            </p>
          </>
        ) : phase === 'paid' ? (
          <>
            <div className="w-14 h-14 rounded-full bg-green-100 dark:bg-green-500/15 flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="w-8 h-8 text-green-600" />
            </div>
            <h1 className="text-2xl font-bold font-display text-gradient-brand">
              {tr('Payment successful', 'გადახდა წარმატებულია')}
            </h1>
            {amount != null && (
              <p className="text-lg font-semibold text-[#1b1033] dark:text-[#f4f0ff] mt-1 tabular-nums">
                {formatMoney(amount)} {currency}
              </p>
            )}

            <div className="mt-5 rounded-2xl bg-[#fdeee9] dark:bg-[#281b47] border border-[#1b1033]/5 dark:border-white/10 p-4 text-left">
              <p className="text-[11px] uppercase tracking-wide text-[#4a3d66] dark:text-[#c3b8dd] mb-1">
                {tr('Transaction ID', 'ტრანზაქციის ID')}
              </p>
              <code className="block text-sm font-mono text-[#1b1033] dark:text-[#f4f0ff] break-all">
                {txId}
              </code>
              {data?.masked_card && (
                <p className="text-xs text-[#4a3d66] dark:text-[#c3b8dd] mt-2">{data.masked_card}</p>
              )}
              <Button
                type="button"
                variant="outline"
                onClick={copyDetails}
                className="w-full mt-3 rounded-full border-[#1b1033]/10 bg-white text-[#1b1033] shadow-celebrate-sm hover:shadow-celebrate-md dark:border-white/10 dark:bg-[#1e1438] dark:text-[#f4f0ff]"
                data-testid="copy-transaction"
              >
                {copied ? (
                  <>
                    <Check className="w-4 h-4 mr-2 text-green-600" />
                    {tr('Copied', 'დაკოპირდა')}
                  </>
                ) : (
                  <>
                    <Copy className="w-4 h-4 mr-2" />
                    {tr('Copy details', 'დეტალების კოპირება')}
                  </>
                )}
              </Button>
            </div>

            <p className="text-xs text-[#4a3d66] dark:text-[#c3b8dd] mt-4">
              {tr(
                'Copy anything you need, then close this window.',
                'დააკოპირეთ რაც გჭირდებათ და დახურეთ ეს ფანჯარა.',
              )}
            </p>
          </>
        ) : (
          <>
            <div className="w-14 h-14 rounded-full bg-red-100 dark:bg-red-500/15 flex items-center justify-center mx-auto mb-4">
              <XCircle className="w-8 h-8 text-red-600" />
            </div>
            <h1 className="text-2xl font-bold text-[#1b1033] dark:text-[#f4f0ff] font-display">
              {tr('Payment not completed', 'გადახდა ვერ დასრულდა')}
            </h1>
            <p className="text-sm text-[#4a3d66] dark:text-[#c3b8dd] mt-2">
              {tr('No charge was made. You can try again.', 'თანხა არ ჩამოჭრილა. შეგიძლიათ თავიდან სცადოთ.')}
            </p>
          </>
        )}

        <Button
          type="button"
          onClick={close}
          className="w-full h-12 mt-5 btn-gradient text-white rounded-full font-semibold"
          data-testid="close-payment-window"
        >
          <X className="w-4 h-4 mr-2" />
          {tr('Close', 'დახურვა')}
        </Button>
      </div>
    </div>
  )
}

export default PaymentComplete
